import React, { FC } from "react";
import strings from "localization/strings";
import { MetaformMember, MetaformMemberGroup } from "generated/client";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import UsersTableGroups from "./users-table-groups";

/**
 * Component props
 */
interface Props {
  loading: boolean;
  loadingMemberId?: string;
  members: MetaformMember[];
  memberGroups: MetaformMemberGroup[];
  selectedMetaformId?: string;
  selectedMemberGroupId: string | undefined;
  onGroupMembershipAdd: (member: MetaformMember, groupId: string) => void;
  onGroupMembershipRemove: (member: MetaformMember, groupId: string) => void;
}

/**
 * Users table
*/
const UsersTable: FC<Props> = ({
  loading,
  loadingMemberId,
  members,
  memberGroups,
  selectedMetaformId,
  selectedMemberGroupId,
  onGroupMembershipAdd,
  onGroupMembershipRemove
}: Props) => {
  const selectedGroup = selectedMemberGroupId ? memberGroups.find(group => group.id === selectedMemberGroupId) : null;

  const visibleMembers = members
    .filter(member => {
      if (!selectedGroup) {
        return true;
      }

      return selectedGroup.memberIds.includes(member.id!);
    });
  let emptyStateMessage = strings.userManagementScreen.selector.form;
  if (selectedMetaformId) {
    emptyStateMessage = selectedMemberGroupId ?
      strings.userManagementScreen.selector.group :
      strings.userManagementScreen.selector.noMembers;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: "#e2e5e3", borderRadius: 1, overflow: "hidden"
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: { xs: 720, sm: 0 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(150px, 1fr) minmax(220px, 1.4fr) minmax(260px, 2fr)",
              gap: 2,
              px: 2.25,
              py: 1.25,
              backgroundColor: "#fafbfa",
              borderBottom: "1px solid #e2e5e3",
              color: "#6e7472",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.7px",
              textTransform: "uppercase"
            }}
          >
            <Box>{ strings.userManagementScreen.usersTable.nameColumn.label }</Box>
            <Box>{ strings.userManagementScreen.usersTable.emailColumn.label }</Box>
            <Box>{ strings.userManagementScreen.usersTable.groupsColumn.label }</Box>
          </Box>
          { loading && <Box display="flex" justifyContent="center" p={ 4 }><CircularProgress size={ 28 }/></Box> }
          { !loading && !visibleMembers.length &&
            <Typography
              sx={{
                color: "#8a8f8c", fontSize: 13, p: 2.25
              }}
            >
              { emptyStateMessage }
            </Typography>
          }
          { !loading && visibleMembers.map(member => (
            <Box
              key={ member.id }
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(150px, 1fr) minmax(220px, 1.4fr) minmax(260px, 2fr)",
                gap: 2,
                alignItems: "center",
                px: 2.25,
                py: 1.25,
                borderTop: "1px solid #f0f2f1"
              }}
            >
              <Typography
                sx={{
                  fontSize: 14, fontWeight: 500, lineHeight: "20px"
                }}
              >
                { `${member.lastName}, ${member.firstName}` }
              </Typography>
              <Typography
                sx={{
                  color: "#4d4d4d", fontSize: 13, lineHeight: "20px"
                }}
              >
                { member.email }
              </Typography>
              <Box minWidth={ 0 }>
                { loadingMemberId === member.id ?
                  <CircularProgress size={ 20 }/> :
                  <UsersTableGroups
                    metaformMember={ member }
                    metaformMemberGroups={ memberGroups }
                    onMetaformGroupMembershipAdd={ onGroupMembershipAdd }
                    onMetaformGroupMembershipRemove={ onGroupMembershipRemove }
                  />
                }
              </Box>
            </Box>
          )) }
        </Box>
      </Box>
    </Paper>
  );
};

export default UsersTable;